import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Cable, ExternalLink, Trash2, Plus, GraduationCap, Dumbbell, CheckCircle2 } from "lucide-react";

export default function AdminIntegrations() {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [cpdAccounts, setCpdAccounts] = useState([]);
  const [newProv, setNewProv] = useState({ id: "", name: "", base_url: "", api_key: "", clinician_launch_url: "", description: "", enabled: true });
  const [newCpd, setNewCpd] = useState({ provider: "", login_url: "", notes: "" });
  const [savingProv, setSavingProv] = useState(false);
  const [savingCpd, setSavingCpd] = useState(false);
  const [showAddProv, setShowAddProv] = useState(false);

  const refresh = async () => {
    const [p, c] = await Promise.all([
      api.get("/admin/exercise-providers"),
      api.get("/cpd-accounts"),
    ]);
    setProviders(p.data); setCpdAccounts(c.data);
  };
  useEffect(() => { refresh(); }, []);

  const saveProvider = async (provider, patch) => {
    const payload = { ...provider, ...patch };
    // don't resend the placeholder api_key field
    if (!patch.api_key) delete payload.api_key;
    try {
      await api.post("/admin/exercise-providers", payload);
      toast.success(`${provider.name} updated`);
      refresh();
    } catch { toast.error("Failed to save"); }
  };

  const addProvider = async () => {
    if (!newProv.id || !newProv.name) return toast.error("Slug & name required");
    setSavingProv(true);
    try {
      await api.post("/admin/exercise-providers", newProv);
      toast.success("Provider added");
      setNewProv({ id: "", name: "", base_url: "", api_key: "", clinician_launch_url: "", description: "", enabled: true });
      setShowAddProv(false);
      refresh();
    } catch { toast.error("Failed"); }
    setSavingProv(false);
  };

  const removeProvider = async (id) => {
    if (!window.confirm("Remove this provider?")) return;
    await api.delete(`/admin/exercise-providers/${id}`);
    toast.success("Removed");
    refresh();
  };

  const addCpd = async () => {
    if (!newCpd.provider || !newCpd.login_url) return toast.error("Provider & URL required");
    setSavingCpd(true);
    try {
      await api.post("/cpd-accounts", newCpd);
      toast.success("CPD account added");
      setNewCpd({ provider: "", login_url: "", notes: "" });
      refresh();
    } catch { toast.error("Failed"); }
    setSavingCpd(false);
  };

  const removeCpd = async (id) => {
    if (!window.confirm("Remove this CPD account?")) return;
    await api.delete(`/cpd-accounts/${id}`);
    toast.success("Removed");
    refresh();
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.aIntegrations.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.aIntegrations.h1")}</h1>
        <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
          External exercise libraries, CPD accounts and clinic tools.
        </p>
      </div>

      {/* Exercise providers */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Dumbbell className="text-[#f47a20]" size={20} />
            <div className="fl-heading text-xl">Exercise libraries</div>
          </div>
          <button className="fl-btn text-xs" onClick={() => setShowAddProv(true)} data-testid="add-provider-btn">
            <Plus size={12} /> Add provider
          </button>
        </div>

        <div className="space-y-3 fl-stagger">
          {providers.map((p) => (
            <div key={p.id} className="fl-card p-5" data-testid={`provider-${p.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <div className="fl-heading text-lg">{p.name}</div>
                    {p.enabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-500">Enabled</span>}
                    {p.api_key_set && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">Key set</span>}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>{p.description || p.base_url}</div>
                </div>
                <div className="flex items-center gap-2">
                  {p.base_url && <a href={p.base_url} target="_blank" rel="noreferrer" className="fl-btn-ghost text-xs"><ExternalLink size={12} /> Open</a>}
                  <button className="text-zinc-500 hover:text-red-500" onClick={() => removeProvider(p.id)} title="Remove"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <div>
                  <div className="fl-label mb-1">Base URL</div>
                  <input className="fl-input" defaultValue={p.base_url || ""} onBlur={(e) => e.target.value !== (p.base_url || "") && saveProvider(p, { base_url: e.target.value })} data-testid={`prov-baseurl-${p.id}`} />
                </div>
                <div>
                  <div className="fl-label mb-1">API key {p.api_key_set && <span className="text-[10px] text-green-500">(set — leave blank to keep)</span>}</div>
                  <input className="fl-input" type="password" placeholder={p.api_key_set ? "•••••••" : "Enter API key"} onBlur={(e) => e.target.value && saveProvider(p, { api_key: e.target.value })} data-testid={`prov-key-${p.id}`} />
                </div>
                <div>
                  <div className="fl-label mb-1">Clinician launch URL</div>
                  <input className="fl-input" defaultValue={p.clinician_launch_url || ""} onBlur={(e) => e.target.value !== (p.clinician_launch_url || "") && saveProvider(p, { clinician_launch_url: e.target.value })} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={p.enabled} onChange={(e) => saveProvider(p, { enabled: e.target.checked })} data-testid={`prov-enable-${p.id}`} /> Enabled — clinicians see this in their launcher
                  </label>
                </div>
              </div>
            </div>
          ))}
          {providers.length === 0 && <div className="text-sm" style={{ color: "var(--fl-muted-2)" }}>No providers yet.</div>}
        </div>
      </section>

      {/* CPD accounts */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap className="text-[#f47a20]" size={20} />
          <div className="fl-heading text-xl">CPD accounts</div>
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>
          Bookmark your clinic's CPD subscriptions. Clinicians see a launcher on their dashboard.
        </p>

        <div className="fl-card p-4 mb-4">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <div className="fl-label mb-1">Provider</div>
              <input className="fl-input" value={newCpd.provider} onChange={(e) => setNewCpd({ ...newCpd, provider: e.target.value })} placeholder="Physiopedia Plus" data-testid="new-cpd-provider" />
            </div>
            <div className="md:col-span-2">
              <div className="fl-label mb-1">Login URL</div>
              <input className="fl-input" value={newCpd.login_url} onChange={(e) => setNewCpd({ ...newCpd, login_url: e.target.value })} placeholder="https://…" data-testid="new-cpd-url" />
            </div>
            <button className="fl-btn" onClick={addCpd} disabled={savingCpd} data-testid="new-cpd-save">
              <Plus size={14} /> {savingCpd ? "Saving…" : "Add"}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {cpdAccounts.map((a) => (
            <div key={a.id} className="fl-card p-4 flex items-center justify-between" data-testid={`cpd-${a.id}`}>
              <div>
                <div className="fl-heading text-sm">{a.provider}</div>
                <a href={a.login_url} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--fl-orange)] hover:underline">{a.login_url}</a>
                {a.notes && <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>{a.notes}</div>}
              </div>
              <button onClick={() => removeCpd(a.id)} className="text-zinc-500 hover:text-red-500" title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {/* Add-provider dialog */}
      {showAddProv && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAddProv(false)}>
          <div className="fl-card p-6 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="fl-heading text-xl mb-4">Add exercise provider</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="fl-label mb-1">Slug (id)</div>
                <input className="fl-input" value={newProv.id} onChange={(e) => setNewProv({ ...newProv, id: e.target.value })} placeholder="e.g. physiotools" data-testid="np-id" />
              </div>
              <div>
                <div className="fl-label mb-1">Name</div>
                <input className="fl-input" value={newProv.name} onChange={(e) => setNewProv({ ...newProv, name: e.target.value })} placeholder="PhysioTools" data-testid="np-name" />
              </div>
              <div>
                <div className="fl-label mb-1">Base URL</div>
                <input className="fl-input" value={newProv.base_url} onChange={(e) => setNewProv({ ...newProv, base_url: e.target.value })} placeholder="https://…" />
              </div>
              <div>
                <div className="fl-label mb-1">API key (optional)</div>
                <input className="fl-input" type="password" value={newProv.api_key} onChange={(e) => setNewProv({ ...newProv, api_key: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">Description</div>
                <input className="fl-input" value={newProv.description} onChange={(e) => setNewProv({ ...newProv, description: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={newProv.enabled} onChange={(e) => setNewProv({ ...newProv, enabled: e.target.checked })} /> Enable immediately
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="fl-btn-ghost" onClick={() => setShowAddProv(false)}>Cancel</button>
              <button className="fl-btn" onClick={addProvider} disabled={savingProv} data-testid="np-save">{savingProv ? "Saving…" : "Add"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
